import { toConversationSlug, toTurnSlug } from "../Chat/chatUtils";

// Chat-write provenance (#11499): the agentic RMS write tool stamps the
// conversation/turn that authorized the write on every fact row it inserts.
// Two surfaces render it — the entity Facts tab's Source chip and the grid's
// right-click Source entry (#11677) — and they must agree on where it points
// and what it says, so the derivation lives here rather than in either one.

export interface FactSourceFields {
  sourceConversationId?: string | null;
  sourceTurnId?: string | null;
  sourceViewable?: boolean | null;
  sourceAuthorizedBy?: string | null;
  // Stated by the backend; never inferred from which ids happen to be
  // non-null. An agents-product fact has a Source only through a cited
  // document; when its run was deleted the conversation entry degrades to
  // the attribution rather than falling through to a wrong-product /chat/
  // link.
  sourceProduct?: "chat" | "agents" | null;
  sourceAgentId?: string | null;
  sourceRunId?: string | null;
  sourceSessionId?: string | null;
  // The first document the agent cited (#14584), resolved by the backend
  // only when the viewer may read it.
  sourceDocumentId?: string | null;
  sourceDocumentName?: string | null;
  sourcePageNum?: number | null;
  sourceInteractionId?: string | null;
}

// The document a value came from, in the shape `openChatCitation` takes, so
// the Source chip and the grid menu open it exactly as a chat citation does.
export interface FactSourceDocument {
  docId: string;
  pageNum: number | null;
  label: string;
}

export interface FactSource {
  // Where the source lives, or null when there's nothing to open (the
  // conversation is gone, or a document-cited agent write's run view has no
  // target ids).
  href: string | null;
  // Whether the VIEWING user may open it. A link is rendered only when this
  // is true AND there's an href; otherwise the surface shows `tooltip`.
  viewable: boolean;
  tooltip: string;
  // The document the agent cited for the value (#14584). When set, the
  // surfaces open it instead of the conversation; null otherwise.
  document: FactSourceDocument | null;
}

// The scalar's source, or — for an array attribute — the first member's that
// has one. Members can come from different writes, and a member without a
// source (typed in, or an agent's with no document) must not hide a later
// member that has one.
export const factSourceFor = (
  value: FactSourceFields | readonly FactSourceFields[] | null | undefined
): FactSource | null => {
  if (!value) return null;
  if (Array.isArray(value)) {
    for (const member of value as readonly FactSourceFields[]) {
      const source = member ? memberSource(member) : null;
      if (source) return source;
    }
    return null;
  }
  return memberSource(value as FactSourceFields);
};

const memberSource = (source: FactSourceFields): FactSource | null => {
  if (!source.sourceConversationId) return null;
  // A named agent's run is not where a value came from: an agent-written
  // value has a Source only when the agent cited a document the viewer can
  // open. A chat write keeps its conversation, which is the user's own
  // request for the value.
  if (
    source.sourceProduct === "agents" &&
    !(source.sourceDocumentId && source.sourceDocumentName)
  ) {
    return null;
  }
  const turnAnchor = source.sourceTurnId
    ? `?turn=${toTurnSlug(source.sourceTurnId)}`
    : "";
  const href =
    source.sourceProduct === "agents"
      ? source.sourceAgentId && source.sourceRunId && source.sourceSessionId
        ? `/agents/${source.sourceAgentId}/runs/${source.sourceRunId}` +
          `/conversations/${source.sourceSessionId}${turnAnchor}`
        : null
      : source.sourceConversationId
        ? `/chat/${toConversationSlug(source.sourceConversationId)}${turnAnchor}`
        : null;
  return {
    href,
    viewable: !!source.sourceViewable,
    tooltip: source.sourceAuthorizedBy
      ? `Set by Finpilot AI, authorized by ${source.sourceAuthorizedBy}.`
      : "Set by Finpilot AI.",
    // The backend resolves the id and the name together (#14584).
    document:
      source.sourceDocumentId && source.sourceDocumentName
        ? {
            docId: source.sourceDocumentId,
            pageNum: source.sourcePageNum ?? null,
            label: source.sourceDocumentName,
          }
        : null,
  };
};
